import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  Clock3,
  Edit3,
  ExternalLink,
  FileText,
  History,
  MessageSquare,
  Plus,
  Save,
  Search,
  Send,
  TimerReset,
  UploadCloud,
  X,
} from 'lucide-react';
import { getStoredAuth, getSubmission, listSubmissions, updateSubmission } from '../lib/auth';
import Badge from '../components/ui/Badge.jsx';

const statusConfig = {
  draft: { label: 'Draft', icon: FileText, className: 'bg-bg-tag text-slate' },
  submitted: { label: 'Submitted', icon: Send, className: 'bg-blue-100 text-blue-800' },
  'under-review': { label: 'Under Review', icon: Clock3, className: 'bg-amber-tint text-amber-tinttext' },
  'revision-required': { label: 'Revision Required', icon: TimerReset, className: 'bg-orange-100 text-orange-800' },
  accepted: { label: 'Accepted', icon: CheckCircle2, className: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', icon: FileText, className: 'bg-red-100 text-red-800' },
  withdrawn: { label: 'Withdrawn', icon: FileText, className: 'bg-gray-100 text-gray-700' },
  published: { label: 'Published', icon: CheckCircle2, className: 'bg-purple-100 text-purple-800' },
};

const editableStatuses = new Set(['draft', 'revision-required']);
const articleTypes = [
  ['original-research', 'Original Research Article'],
  ['review', 'Review Article'],
  ['systematic-review-meta-analysis', 'Systematic Review and Meta-analysis'],
  ['edge', 'EDGE'],
  ['case-report', 'Case Report/Series'],
  ['others', 'Others'],
];

function formatDate(value) {
  if (!value) return 'Not submitted';
  return new Intl.DateTimeFormat('en', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(value));
}

function fileName(file, fallback) {
  return file?.filename || file?.name || fallback;
}

function documentUrl(file) {
  return file?.publicUrl || file?.url;
}

function authorToFields(author) {
  const names = String(author.name || '').trim().split(/\s+/);
  return {
    firstName: names.slice(0, -1).join(' ') || names[0] || '',
    lastName: names.length > 1 ? names.slice(-1).join(' ') : '',
    email: author.email || '',
    affiliation: author.affiliation || '',
    orcid: author.orcid || '',
    isCorresponding: Boolean(author.isCorresponding),
  };
}

function buildEditForm(submission) {
  return {
    status: submission.status,
    articleType: submission.articleType || 'original-research',
    title: submission.title || '',
    abstract: submission.abstract || '',
    keywords: (submission.keywords || []).join(', '),
    authors: (submission.authors || []).length ? submission.authors.map(authorToFields) : [{ firstName: '', lastName: '', email: '', affiliation: '', orcid: '', isCorresponding: true }],
    declarations: {
      officialTemplate: Boolean(submission.declarations?.officialTemplate),
      coverLetterComplete: Boolean(submission.declarations?.coverLetterComplete || submission.declarations?.coverPageComplete),
      ethics: Boolean(submission.declarations?.ethics),
      journalPolicies: Boolean(submission.declarations?.journalPolicies),
    },
  };
}

function Dashboard() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editFiles, setEditFiles] = useState({ coverLetter: null, manuscript: null, replyToReviewer: null });
  const [saving, setSaving] = useState(false);
  const auth = getStoredAuth();

  const load = async () => {
    setLoading(true);
    try {
      const result = await listSubmissions();
      setSubmissions(result.data || []);
      setMessage('');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'Author Dashboard - JAMSD';
    if (auth?.token) load();
  }, [auth?.token]);

  const filtered = useMemo(() => submissions.filter((item) => {
    const matchesStatus = !status || item.status === status;
    const haystack = `${item.submissionId || ''} ${item.title || ''} ${item.articleType || ''}`.toLowerCase();
    return matchesStatus && haystack.includes(query.toLowerCase());
  }), [submissions, status, query]);

  const counts = useMemo(() => submissions.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {}), [submissions]);

  const openSubmission = async (id) => {
    setDetailLoading(true);
    setMessage('');
    try {
      const result = await getSubmission(id);
      setSelected(result.data);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const openEdit = () => {
    setEditForm(buildEditForm(selected));
    setEditFiles({ coverLetter: null, manuscript: null, replyToReviewer: null });
    setEditOpen(true);
  };

  const updateAuthor = (index, key, value) => {
    setEditForm((prev) => ({
      ...prev,
      authors: prev.authors.map((author, idx) => (idx === index ? { ...author, [key]: value } : author)),
    }));
  };

  const submitEdit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const body = new FormData();
      body.append('articleType', editForm.articleType);
      body.append('title', editForm.title);
      body.append('abstract', editForm.abstract);
      body.append('keywords', JSON.stringify(editForm.keywords.split(',').map((item) => item.trim()).filter(Boolean)));
      body.append('authors', JSON.stringify(editForm.authors.map((author) => ({
        name: `${author.firstName} ${author.lastName}`.trim(),
        email: author.email,
        affiliation: author.affiliation,
        orcid: author.orcid,
        isCorresponding: author.isCorresponding,
      }))));
      body.append('declarations', JSON.stringify({
        authorship: true,
        conflictOfInterest: true,
        officialTemplate: editForm.declarations.officialTemplate,
        coverLetterComplete: editForm.declarations.coverLetterComplete,
        coverPageComplete: editForm.declarations.coverLetterComplete,
        ethics: editForm.declarations.ethics,
        journalPolicies: editForm.declarations.journalPolicies,
      }));
      if (editFiles.coverLetter) body.append('coverLetter', editFiles.coverLetter);
      if (editFiles.manuscript) body.append('manuscript', editFiles.manuscript);
      if (editFiles.replyToReviewer) body.append('replyToReviewer', editFiles.replyToReviewer);

      await updateSubmission(selected._id, body);
      const refreshed = await getSubmission(selected._id);
      setSelected(refreshed.data);
      setEditOpen(false);
      await load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!auth?.token) {
    return (
      <div className="max-w-4xl mx-auto px-4 pt-28 pb-16">
        <h1 className="font-display text-navy text-3xl font-bold mb-3">Dashboard</h1>
        <Link to="/auth/login" className="btn-amber px-5 py-3">Sign In</Link>
      </div>
    );
  }

  return (
    <>
      <section className="bg-navy pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <p className="font-sans text-amber text-xs font-semibold uppercase tracking-widest">Author Workspace</p>
              <h1 className="font-display text-white text-3xl md:text-4xl font-bold mt-2">My Manuscripts</h1>
              <p className="font-sans text-slate-light mt-3 max-w-2xl">
                Track submitted manuscripts, read editorial comments, and review the full audit history for every submission.
              </p>
            </div>
            {auth.user.role === 'author' && (
              <Link to="/submit" className="btn-amber px-5 py-3 inline-flex items-center justify-center gap-2">
                <Plus className="h-4 w-4" aria-hidden="true" />
                New Submission
              </Link>
            )}
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {message && <div className="mb-5 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{message}</div>}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {['submitted', 'under-review', 'revision-required', 'accepted', 'draft'].map((key) => {
            const config = statusConfig[key];
            const Icon = config.icon;
            return (
              <button key={key} type="button" onClick={() => setStatus(status === key ? '' : key)} className={`rounded-lg border bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${status === key ? 'border-amber ring-2 ring-amber/20' : 'border-bg-border'}`}>
                <Icon className="h-5 w-5 text-amber mb-3" aria-hidden="true" />
                <p className="font-display text-3xl font-bold text-navy">{counts[key] || 0}</p>
                <p className="font-sans text-xs uppercase tracking-wide text-slate mt-1">{config.label}</p>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">
          <section className="space-y-5">
            <div className="rounded-lg border border-bg-border bg-white p-4 shadow-sm">
              <div className="flex flex-col md:flex-row gap-3">
                <label className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate" aria-hidden="true" />
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search by title, ID, or article type"
                    className="w-full rounded border border-bg-border pl-10 pr-3 py-2.5 font-sans text-sm"
                  />
                </label>
                <select value={status} onChange={(event) => setStatus(event.target.value)} className="rounded border border-bg-border px-3 py-2.5 font-sans text-sm md:w-56">
                  <option value="">All statuses</option>
                  {Object.entries(statusConfig).map(([key, config]) => <option key={key} value={key}>{config.label}</option>)}
                </select>
              </div>
            </div>

            {loading && <div className="rounded-lg border border-bg-border bg-white p-8 text-center font-sans text-slate">Loading your submissions...</div>}

            {!loading && filtered.map((item) => {
              const config = statusConfig[item.status] || statusConfig.submitted;
              return (
                <article key={item._id} className="rounded-lg border border-bg-border bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <button type="button" onClick={() => openSubmission(item._id)} className="min-w-0 text-left">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`rounded-full px-3 py-1 text-xs font-sans font-semibold ${config.className}`}>{config.label}</span>
                        <Badge type={item.articleType} />
                      </div>
                      <p className="font-sans text-xs uppercase tracking-wide text-slate">{item.submissionId || 'Draft manuscript'}</p>
                      <h2 className="font-display text-navy text-xl font-bold mt-1">{item.title || 'Untitled draft'}</h2>
                      <p className="font-sans text-sm text-slate mt-3">
                        Submitted {formatDate(item.submittedAt)} · Last updated {formatDate(item.updatedAt)}
                      </p>
                    </button>
                    <div className="rounded border border-bg-border bg-bg-ice p-3 md:w-48">
                      <p className="font-sans text-xs uppercase text-slate">Next Action</p>
                      <p className="font-sans text-sm font-semibold text-navy mt-1">
                        {item.status === 'revision-required' ? 'Prepare revision' : item.status === 'accepted' ? 'Published as article' : item.status === 'draft' ? 'Complete submission' : 'Await editorial update'}
                      </p>
                      {item.status === 'draft' ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/submit?draft=${item._id}`)}
                          className="mt-3 text-sm font-semibold text-amber"
                        >
                          Continue Draft →
                        </button>
                      ) : (
                        <button type="button" onClick={() => openSubmission(item._id)} className="mt-3 text-sm font-semibold text-amber">View details</button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}

            {!loading && !filtered.length && (
              <div className="rounded-lg border border-bg-border bg-bg-ice p-10 text-center">
                <p className="font-display text-navy text-xl font-bold">No manuscripts found</p>
                <p className="font-sans text-sm text-slate mt-2">Try changing your filters or start a new submission.</p>
              </div>
            )}
          </section>

          <aside className="space-y-5 lg:sticky lg:top-24">
            <div className="rounded-lg border border-bg-border bg-white p-5 shadow-sm">
              <p className="font-sans text-xs uppercase tracking-widest text-amber font-semibold">Profile</p>
              <h2 className="font-display text-navy text-xl font-bold mt-2">{auth.user.fullName}</h2>
              <p className="font-sans text-sm text-slate mt-1">{auth.user.email}</p>
              <p className="font-sans text-xs text-gray-500 mt-3">{auth.user.affiliation || 'Affiliation not provided'}</p>
            </div>

            <div className="rounded-lg border border-bg-border bg-white p-5 shadow-sm">
              <p className="font-sans text-xs uppercase tracking-widest text-amber font-semibold mb-4">Submission Checklist</p>
              {['Download official templates', 'Prepare cover letter and manuscript', 'Confirm ethics approval', 'Upload both required files'].map((item) => (
                <div key={item} className="flex items-center gap-3 py-2 border-b border-bg-border last:border-b-0">
                  <CheckCircle2 className="h-4 w-4 text-amber flex-none" aria-hidden="true" />
                  <span className="font-sans text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </main>

      {detailLoading && <Modal title="Loading Submission" onClose={() => setDetailLoading(false)}><p className="text-sm text-slate">Fetching submission details...</p></Modal>}
      {selected && !editOpen && (
        <SubmissionDetail
          submission={selected}
          onClose={() => setSelected(null)}
          onEdit={editableStatuses.has(selected.status) ? openEdit : null}
        />
      )}
      {editOpen && editForm && (
        <EditSubmissionModal
          form={editForm}
          setForm={setEditForm}
          files={editFiles}
          setFiles={setEditFiles}
          saving={saving}
          onAuthorChange={updateAuthor}
          onSubmit={submitEdit}
          onClose={() => setEditOpen(false)}
        />
      )}
    </>
  );
}

function SubmissionDetail({ submission, onClose, onEdit }) {
  const config = statusConfig[submission.status] || statusConfig.submitted;
  const latestComment = [...(submission.auditLog || [])].reverse().find((entry) => entry.comments && entry.action === 'editorial-decision');

  return (
    <Modal title={submission.submissionId || 'Draft manuscript'} onClose={onClose} wide>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${config.className}`}>{config.label}</span>
            <h2 className="mt-3 font-display text-2xl font-bold text-navy">{submission.title || 'Untitled draft'}</h2>
            <p className="mt-2 text-sm text-slate">Submitted {formatDate(submission.submittedAt)} · Last updated {formatDate(submission.updatedAt)}</p>
          </div>
          {onEdit && (
            <button type="button" onClick={onEdit} className="btn-amber inline-flex items-center justify-center gap-2 px-4 py-2">
              <Edit3 className="h-4 w-4" />
              Edit Submission
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <InfoPanel title="Manuscript">
            <p className="text-sm text-slate mb-3" dangerouslySetInnerHTML={{ __html: submission.abstract || 'No abstract provided.' }} />
            <p className="text-xs uppercase text-slate">Keywords</p>
            <p className="text-sm text-navy">{(submission.keywords || []).join(', ') || 'Not provided'}</p>
          </InfoPanel>
          <InfoPanel title="Documents">
            <DocumentLink file={submission.manuscriptFile} label="Manuscript" />
            <DocumentLink file={submission.coverPageFile} label="Cover Letter" />
            {(submission.revisions || []).map((revision, index) => (
              revision.replyToReviewerFile ? <DocumentLink key={index} file={revision.replyToReviewerFile} label={`Reply to Reviewer (Revision ${index + 1})`} /> : null
            ))}
          </InfoPanel>
        </div>

        <InfoPanel title="Authors">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(submission.authors || []).map((author, index) => (
              <div key={`${author.email}-${index}`} className="rounded border border-bg-border bg-bg-ice p-3">
                <p className="font-semibold text-navy">{author.name || 'Unnamed author'} {author.isCorresponding ? <span className="text-xs text-amber">Corresponding</span> : null}</p>
                <p className="text-sm text-slate">{author.email}</p>
                <p className="text-xs text-gray-500">{author.affiliation}</p>
              </div>
            ))}
          </div>
        </InfoPanel>

        <InfoPanel title="Admin Comment">
          <div className="flex gap-3">
            <MessageSquare className="h-5 w-5 text-amber flex-none" />
            <p className="text-sm text-slate">{latestComment?.comments || 'No admin comment has been added yet.'}</p>
          </div>
        </InfoPanel>

        <InfoPanel title="Audit Logs">
          <AuditLog entries={submission.auditLog || []} />
        </InfoPanel>
      </div>
    </Modal>
  );
}

function EditSubmissionModal({ form, setForm, files, setFiles, saving, onAuthorChange, onSubmit, onClose }) {
  return (
    <Modal title="Edit Submission" onClose={onClose} wide>
      <form onSubmit={onSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-navy md:col-span-2">Title
            <input value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} className="mt-1 w-full rounded border border-bg-border px-3 py-2" required />
          </label>
          <label className="text-xs font-semibold text-navy">Article Type
            <select value={form.articleType} onChange={(event) => setForm((prev) => ({ ...prev, articleType: event.target.value }))} className="mt-1 w-full rounded border border-bg-border px-3 py-2">
              {articleTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="text-xs font-semibold text-navy">Keywords
            <input value={form.keywords} onChange={(event) => setForm((prev) => ({ ...prev, keywords: event.target.value }))} className="mt-1 w-full rounded border border-bg-border px-3 py-2" placeholder="Comma separated" />
          </label>
          <label className="text-xs font-semibold text-navy md:col-span-2">Abstract
            <textarea value={form.abstract} onChange={(event) => setForm((prev) => ({ ...prev, abstract: event.target.value }))} rows={5} className="mt-1 w-full rounded border border-bg-border px-3 py-2" required />
          </label>
        </div>

        <InfoPanel title="Authors">
          <div className="space-y-3">
            {form.authors.map((author, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded border border-bg-border bg-bg-ice p-3">
                <input value={author.firstName} onChange={(event) => onAuthorChange(index, 'firstName', event.target.value)} placeholder="First name" className="rounded border border-bg-border px-3 py-2" />
                <input value={author.lastName} onChange={(event) => onAuthorChange(index, 'lastName', event.target.value)} placeholder="Last name" className="rounded border border-bg-border px-3 py-2" />
                <input value={author.email} onChange={(event) => onAuthorChange(index, 'email', event.target.value)} placeholder="Email" className="rounded border border-bg-border px-3 py-2" />
                <input value={author.affiliation} onChange={(event) => onAuthorChange(index, 'affiliation', event.target.value)} placeholder="Affiliation" className="rounded border border-bg-border px-3 py-2" />
                <label className="flex items-center gap-2 text-xs text-navy">
                  <input type="radio" checked={author.isCorresponding} onChange={() => setForm((prev) => ({ ...prev, authors: prev.authors.map((item, idx) => ({ ...item, isCorresponding: idx === index })) }))} />
                  Corresponding author
                </label>
              </div>
            ))}
          </div>
        </InfoPanel>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FileInput label="Replace Cover Letter" file={files.coverLetter} onChange={(file) => setFiles((prev) => ({ ...prev, coverLetter: file }))} />
          <FileInput label="Replace Manuscript" file={files.manuscript} onChange={(file) => setFiles((prev) => ({ ...prev, manuscript: file }))} />
          {form.status === 'revision-required' && (
            <div className="md:col-span-2">
              <FileInput label="Reply to Reviewer (optional)" file={files.replyToReviewer} onChange={(file) => setFiles((prev) => ({ ...prev, replyToReviewer: file }))} />
              <a href="/templates/Reply_to_Reviewer_Template.docx" download className="mt-1 inline-block text-xs font-semibold text-navy hover:text-amber">
                Download Reply to Reviewer template
              </a>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            ['officialTemplate', 'Official template used'],
            ['coverLetterComplete', 'Cover letter is complete'],
            ['ethics', 'Ethics declaration confirmed'],
            ['journalPolicies', 'Journal policies accepted'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm text-slate">
              <input type="checkbox" checked={form.declarations[key]} onChange={(event) => setForm((prev) => ({ ...prev, declarations: { ...prev.declarations, [key]: event.target.checked } }))} />
              {label}
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded border border-bg-border px-4 py-2">Cancel</button>
          <button type="submit" disabled={saving} className="btn-amber inline-flex items-center gap-2 px-5 py-2 disabled:opacity-60">
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ title, children, onClose, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm">
      <div className={`max-h-[90vh] w-full overflow-y-auto rounded-lg bg-white shadow-2xl ${wide ? 'max-w-5xl' : 'max-w-md'}`}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-bg-border bg-white px-5 py-4">
          <h3 className="font-display text-xl font-bold text-navy">{title}</h3>
          <button type="button" onClick={onClose} className="rounded p-2 text-slate hover:bg-bg-ice" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function InfoPanel({ title, children }) {
  return (
    <section className="rounded border border-bg-border bg-white p-4">
      <h3 className="mb-3 font-sans text-xs font-bold uppercase tracking-wide text-slate">{title}</h3>
      {children}
    </section>
  );
}

function DocumentLink({ file, label }) {
  const url = documentUrl(file);
  return (
    <div className="mb-3 rounded border border-bg-border bg-bg-ice p-3 last:mb-0">
      <p className="text-xs uppercase text-slate">{label}</p>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-2 font-semibold text-navy hover:text-amber">
          <FileText className="h-4 w-4" />
          {fileName(file, label)}
          <ExternalLink className="h-4 w-4" />
        </a>
      ) : (
        <p className="mt-1 text-sm text-slate">No file uploaded.</p>
      )}
    </div>
  );
}

function AuditLog({ entries }) {
  if (!entries.length) return <p className="text-sm text-slate">No audit activity recorded.</p>;
  return (
    <div className="space-y-3">
      {entries.slice().reverse().map((entry, index) => (
        <div key={`${entry.createdAt}-${index}`} className="flex gap-3 rounded border border-bg-border bg-bg-ice p-3">
          <History className="h-5 w-5 flex-none text-amber" />
          <div>
            <p className="text-sm font-semibold text-navy">{String(entry.action || 'activity').replace(/-/g, ' ')}</p>
            <p className="text-xs text-slate">
              {formatDate(entry.createdAt)} · {entry.actor?.fullName || 'System'}
              {entry.fromStatus || entry.toStatus ? ` · ${entry.fromStatus || 'new'} to ${entry.toStatus || 'current'}` : ''}
            </p>
            {entry.comments && <p className="mt-1 text-sm text-gray-700">{entry.comments}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function FileInput({ label, file, onChange }) {
  return (
    <label className="block rounded border border-dashed border-bg-border bg-bg-ice p-4 text-center text-sm text-slate">
      <UploadCloud className="mx-auto mb-2 h-6 w-6 text-amber" />
      {label}
      <input type="file" accept=".doc,.docx,.pdf" onChange={(event) => onChange(event.target.files?.[0] || null)} className="mt-3 w-full rounded border border-bg-border bg-white px-3 py-2 text-left" />
      {file && <span className="mt-2 block text-xs text-navy">{file.name}</span>}
    </label>
  );
}

export default Dashboard;
